import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Users, Shield, Truck, Eye, EyeOff, X,
  CheckCircle2, ArrowRight, Loader2, LogOut, Settings,
  Zap, Star, MessageCircle, Send, Paperclip,
  TrendingUp, FileCheck, AlertCircle, Lock,
  ChevronLeft, ChevronRight, Award, Flame, Trophy,
  Calendar, DollarSign, FileText, Trash2, ExternalLink,
  Search, ChevronDown, Upload, Phone, Share2, Camera, Smile, ArrowLeft,
  Check, CheckCircle, XCircle, PhoneOff, RotateCcw,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CarrierDirectory } from "./CarrierProfile";

import { ChatRoom } from "./ChatRoom";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";
const DARK   = "#0A0A0A";
const CARD   = "#161618";
const MID    = "#1C1C1E";
const GREEN_SUCCESS = "#22c55e";

const HUB_META = {
  carrier:   { icon: Building2, label: "Carriers",   accent: "#FF4D00", glow: "255,77,0",    desc: "Bid on drivers",      badge: "HIRING",     badgeBg: "rgba(255,77,0,0.15)" },
  recruiter: { icon: Users,     label: "Recruiters", accent: "#F5C842", glow: "245,200,66",  desc: "Manage pipeline",     badge: "TALENT",     badgeBg: "rgba(245,200,66,0.15)" },
  safety:    { icon: Shield,    label: "Safety",     accent: "#38BDF8", glow: "56,189,248",  desc: "Vet & approve",       badge: "COMPLIANCE", badgeBg: "rgba(56,189,248,0.15)" },
  driver:    { icon: Truck,     label: "Drivers",    accent: "#4ADE80", glow: "74,222,128",  desc: "Your market value",   badge: "PROFILES",   badgeBg: "rgba(74,222,128,0.15)" },
  admin:     { icon: Star,      label: "Admin",      accent: "#A78BFA", glow: "167,139,250", desc: "Full access",         badge: "SYSTEM",     badgeBg: "rgba(167,139,250,0.15)" },
};

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = Date.now() - new Date(isoStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ══════════════════════════════════════════════════════════════════════════════
// ── BULK CDL UPLOAD PANEL ─────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
function BulkCDLPanel({ token, fullDrivers, onPhotoUpdated }) {
  const [showPanel, setShowPanel]         = useState(false);
  const [uploadQueue, setUploadQueue]     = useState({}); // { driverId: File }
  const [statuses, setStatuses]           = useState({}); // { driverId: 'uploading'|'done'|'failed' }
  const [isDragOver, setIsDragOver]       = useState(false);
  const [unmatched, setUnmatched]         = useState([]); // [{file, driverId:''}]
  const [processing, setProcessing]       = useState(false);
  const [croppedPhotos, setCroppedPhotos] = useState({}); // { driverId: photoUrl }
  const dropRef = React.useRef(null);

  const driversNoPhoto = fullDrivers.filter((d) => !d.market_photo_path && d.visible_on_market);

  function normalizeForMatch(str) {
    return str.replace(/\.[^/.]+$/, "").toLowerCase().replace(/[_\-\.]+/g, " ").replace(/\s+/g, " ").trim();
  }

  function matchFileToDriver(filename) {
    const norm = normalizeForMatch(filename);
    for (const d of driversNoPhoto) {
      const fn = d.first_name.toLowerCase();
      const ln = d.last_name.toLowerCase();
      if (norm.includes(fn) && norm.includes(ln)) return d.id;
    }
    return null;
  }

  function handleFiles(files) {
    const imgFiles = Array.from(files).filter((f) => f.type.startsWith("image/") || /\.(jpe?g|png|webp|heic|heif)$/i.test(f.name));
    const newQueue = { ...uploadQueue };
    const newUnmatched = [];
    for (const file of imgFiles) {
      const matched = matchFileToDriver(file.name);
      if (matched && !newQueue[matched]) {
        newQueue[matched] = file;
      } else if (!matched) {
        newUnmatched.push({ file, driverId: "" });
      }
    }
    setUploadQueue(newQueue);
    if (newUnmatched.length) setUnmatched((prev) => [...prev, ...newUnmatched]);
  }

  function onDrop(e) {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  }

  function assignUnmatched(idx, driverId) {
    const updated = unmatched.map((u, i) => i === idx ? { ...u, driverId } : u);
    setUnmatched(updated);
    if (driverId) {
      const file = updated[idx].file;
      setUploadQueue((prev) => ({ ...prev, [driverId]: file }));
      setUnmatched((prev) => prev.filter((_, i) => i !== idx));
    }
  }

  async function processAll() {
    const entries = Object.entries(uploadQueue).filter(([, f]) => f);
    if (!entries.length) return;
    setProcessing(true);
    for (const [driverId, file] of entries) {
      if (statuses[driverId] === "done") continue;
      setStatuses((prev) => ({ ...prev, [driverId]: "uploading" }));
      try {
        const form = new FormData();
        form.append("file", file);
        const res = await api.marketUploadCdlFile(token, driverId, form);
        setStatuses((prev) => ({ ...prev, [driverId]: "done" }));
        if (res.market_photo_path) {
          onPhotoUpdated(driverId, res.market_photo_path);
          const API = process.env.REACT_APP_BACKEND_URL;
          setCroppedPhotos((prev) => ({ ...prev, [driverId]: `${API}/api/market/drivers/${driverId}/photo` }));
        }
        setUploadQueue((prev) => { const n = { ...prev }; delete n[driverId]; return n; });
      } catch {
        setStatuses((prev) => ({ ...prev, [driverId]: "failed" }));
      }
    }
    setProcessing(false);
  }

  const queuedCount = Object.values(uploadQueue).filter(Boolean).length;
  const doneCount   = Object.values(statuses).filter((s) => s === "done").length;

  return (
    <div data-testid="bulk-cdl-panel" style={{ background: MID, border: "1px solid rgba(245,200,66,0.2)" }}>
      <button
        data-testid="bulk-cdl-toggle"
        onClick={() => setShowPanel((v) => !v)}
        className="w-full px-4 py-3 flex items-center gap-3 text-left"
        style={{ borderBottom: showPanel ? "1px solid rgba(255,255,255,0.07)" : "none" }}
      >
        <Camera className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
        <div className="flex-1">
          <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Bulk CDL Upload</p>
          <p className="text-[10px] text-white/30 mt-0.5">Upload CDL images to auto-generate driver profile photos</p>
        </div>
        {doneCount > 0 && (
          <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
            style={{ background: "rgba(74,222,128,0.12)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }}>
            {doneCount} done
          </span>
        )}
        {queuedCount > 0 && (
          <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
            style={{ background: `rgba(245,200,66,0.12)`, color: GOLD, border: `1px solid rgba(245,200,66,0.3)` }}>
            {queuedCount} queued
          </span>
        )}
        <ChevronDown className="w-4 h-4 text-white/30 transition-transform ml-1" style={{ transform: showPanel ? "rotate(180deg)" : "rotate(0deg)" }} />
      </button>

      {showPanel && (
        <div className="p-4 space-y-4">
          {/* Drag-and-drop zone */}
          <div
            ref={dropRef}
            data-testid="bulk-cdl-dropzone"
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={onDrop}
            className="relative flex flex-col items-center justify-center py-8 px-4 text-center transition-all cursor-pointer"
            style={{
              border: `1.5px dashed ${isDragOver ? GOLD : "rgba(255,255,255,0.12)"}`,
              background: isDragOver ? `rgba(245,200,66,0.05)` : "rgba(255,255,255,0.02)",
            }}
            onClick={() => document.getElementById("bulk-cdl-multi-input").click()}
          >
            <input
              id="bulk-cdl-multi-input"
              type="file"
              multiple
              accept="image/*,.heic,.heif"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <Upload className="w-8 h-8 mb-3 transition-colors" style={{ color: isDragOver ? GOLD : "rgba(255,255,255,0.15)" }} />
            <p className="font-head text-xs font-bold uppercase tracking-[0.15em] text-white/60 mb-1">
              Drop CDL images here or click to browse
            </p>
            <p className="text-[10px] text-white/25">
              Files named <span className="font-mono" style={{ color: GOLD }}>"John_Burton_CDL.jpg"</span> auto-match by name
            </p>
          </div>

          {/* Unmatched files */}
          {unmatched.length > 0 && (
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/40 mb-2">Unmatched Files — Assign Manually</p>
              <div className="space-y-2">
                {unmatched.map((u, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="flex-1 text-[10px] text-white/50 truncate font-mono">{u.file.name}</span>
                    <select
                      value={u.driverId}
                      onChange={(e) => assignUnmatched(i, e.target.value)}
                      className="text-xs bg-[#111] border border-white/10 text-white px-2 py-1 focus:outline-none"
                      style={{ minWidth: 160 }}
                    >
                      <option value="">-- Select driver --</option>
                      {driversNoPhoto.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.first_name} {d.last_name} ({d.state})
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Driver grid */}
          {driversNoPhoto.length === 0 ? (
            <p className="text-center text-[11px] text-white/25 py-4">All drivers have profile photos!</p>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/40">
                  Drivers Without Profile Photo ({driversNoPhoto.length})
                </p>
                {queuedCount > 0 && (
                  <button
                    data-testid="bulk-cdl-process-all"
                    onClick={processAll}
                    disabled={processing}
                    className="flex items-center gap-1.5 font-head text-[9px] uppercase tracking-[0.15em] px-3 py-1.5 disabled:opacity-60 transition-all hover:brightness-110"
                    style={{ background: GOLD, color: DARK }}
                  >
                    {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                    Process All ({queuedCount})
                  </button>
                )}
              </div>

              <div className="divide-y divide-white/5">
                {driversNoPhoto.map((d) => {
                  const status = statuses[d.id];
                  const queued = uploadQueue[d.id];
                  const cropPreview = croppedPhotos[d.id];
                  return (
                    <div key={d.id} className="flex items-center gap-3 py-2.5" data-testid={`bulk-cdl-row-${d.id}`}>
                      {/* Avatar — shows cropped face after upload, initials before */}
                      <div
                        className="w-8 h-10 flex items-center justify-center shrink-0 font-head text-xs font-black overflow-hidden"
                        style={{ background: cropPreview ? "#111" : `${ACCENT}20`, color: ACCENT, border: `1px solid ${cropPreview ? "rgba(74,222,128,0.4)" : ACCENT + "30"}` }}
                      >
                        {cropPreview ? (
                          <img src={cropPreview} alt={d.first_name} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} data-testid={`crop-preview-${d.id}`} />
                        ) : (
                          <>{d.first_name[0]}{d.last_name?.[0] || ""}</>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-head text-xs font-bold text-white leading-tight">
                          {d.first_name} {d.last_name}
                        </p>
                        <p className="text-[10px] text-white/30 mt-0.5">{d.state} · {d.cdl_class}</p>
                      </div>
                      {/* Status / action */}
                      <div className="shrink-0">
                        {status === "done" ? (
                          <span className="font-head text-[9px] px-2 py-1 uppercase tracking-[0.1em]"
                            style={{ background: "rgba(74,222,128,0.12)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }}>
                            {cropPreview ? "✓ Face cropped" : "✓ Done"}
                          </span>
                        ) : status === "uploading" ? (
                          <span className="flex items-center gap-1 font-head text-[9px] px-2 py-1 uppercase tracking-[0.1em] text-white/50">
                            <Loader2 className="w-3 h-3 animate-spin" /> Cropping
                          </span>
                        ) : status === "failed" ? (
                          <label className="cursor-pointer">
                            <input type="file" accept="image/*,.heic,.heif" className="hidden"
                              onChange={(e) => { if (e.target.files[0]) setUploadQueue((prev) => ({ ...prev, [d.id]: e.target.files[0] })); }} />
                            <span className="font-head text-[9px] px-2 py-1 uppercase tracking-[0.1em] border border-red-500/30 text-red-400">
                              ✗ Retry
                            </span>
                          </label>
                        ) : queued ? (
                          <span className="font-head text-[9px] px-2 py-1 uppercase tracking-[0.1em] truncate max-w-[120px] block"
                            style={{ background: `rgba(245,200,66,0.1)`, color: GOLD, border: `1px solid rgba(245,200,66,0.3)` }}>
                            ✓ {queued.name.replace(/\.[^/.]+$/, "").slice(0, 12)}…
                          </span>
                        ) : (
                          <label data-testid={`bulk-cdl-choose-${d.id}`} className="cursor-pointer">
                            <input type="file" accept="image/*,.heic,.heif" className="hidden"
                              onChange={(e) => { if (e.target.files[0]) setUploadQueue((prev) => ({ ...prev, [d.id]: e.target.files[0] })); }} />
                            <span className="font-head text-[9px] px-2 py-1 uppercase tracking-[0.1em] border border-white/10 text-white/40 hover:text-white/70 hover:border-white/25 transition-colors">
                              Choose CDL
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── BULK MEDICAL UPLOAD PANEL ─────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
function BulkMedicalPanel({ token, fullDrivers }) {
  const [showPanel, setShowPanel] = useState(false);
  const [statuses, setStatuses] = useState({}); // driverId → "uploading" | "done" | "failed"

  const driversNeedMedical = fullDrivers.filter((d) => !d.has_medical);
  const doneCount = Object.values(statuses).filter((s) => s === "done").length;

  async function uploadMedical(driverId, file) {
    setStatuses((prev) => ({ ...prev, [driverId]: "uploading" }));
    try {
      await api.marketSafetyUploadDoc(token, driverId, "medical", file);
      setStatuses((prev) => ({ ...prev, [driverId]: "done" }));
      toast.success("Medical certificate uploaded.");
    } catch {
      setStatuses((prev) => ({ ...prev, [driverId]: "failed" }));
      toast.error("Upload failed.");
    }
  }

  return (
    <div style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }}>
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="w-full px-4 py-3 flex items-center gap-3 text-left"
        style={{ borderBottom: showPanel ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
        <Upload className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
        <div className="flex-1">
          <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Bulk Medical Upload</p>
          <p className="text-[10px] text-white/30 mt-0.5">Upload medical certificates for drivers missing them</p>
        </div>
        {doneCount > 0 && (
          <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
            style={{ background: "rgba(74,222,128,0.12)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }}>
            {doneCount} done
          </span>
        )}
        <ChevronDown className="w-4 h-4 text-white/30 transition-transform ml-1" style={{ transform: showPanel ? "rotate(180deg)" : "rotate(0deg)" }} />
      </button>
      {showPanel && (
        <div className="p-4">
          {driversNeedMedical.length === 0 ? (
            <p className="text-xs text-white/30 text-center py-4">All drivers have medical certificates on file.</p>
          ) : (
            <div className="divide-y divide-white/5">
              {driversNeedMedical.map((d) => {
                const status = statuses[d.id];
                return (
                  <div key={d.id} className="flex items-center gap-3 py-2.5" data-testid={`bulk-medical-row-${d.id}`}>
                    <div className="w-8 h-10 flex items-center justify-center shrink-0 font-head text-xs font-black"
                      style={{ background: `${ACCENT}20`, color: ACCENT, border: `1px solid ${ACCENT}30` }}>
                      {d.first_name[0]}{d.last_name?.[0] || ""}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-head text-xs font-bold text-white leading-tight">{d.first_name} {d.last_name}</p>
                      <p className="text-[10px] text-white/30 mt-0.5">{d.state} · {d.cdl_class}</p>
                    </div>
                    <div className="shrink-0">
                      {status === "done" ? (
                        <span className="font-head text-[9px] px-2 py-1 uppercase tracking-[0.1em]"
                          style={{ background: "rgba(74,222,128,0.12)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }}>✓ Done</span>
                      ) : status === "uploading" ? (
                        <span className="flex items-center gap-1 font-head text-[9px] px-2 py-1 uppercase text-white/50">
                          <Loader2 className="w-3 h-3 animate-spin" /> Uploading
                        </span>
                      ) : (
                        <label data-testid={`bulk-medical-choose-${d.id}`} className="cursor-pointer">
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMedical(d.id, f); e.target.value = ""; }} />
                          <span className="font-head text-[9px] px-2 py-1 uppercase tracking-[0.1em] border border-white/10 text-white/40 hover:text-white/70 hover:border-white/25 transition-colors">
                            {status === "failed" ? "✗ Retry" : "Upload Medical"}
                          </span>
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── RECRUITER DRIVER LIST (sub-component to comply with Rules of Hooks) ───────
// ══════════════════════════════════════════════════════════════════════════════
function RecruiterDriverList({ driversWithPhone, token, onToggleVisibility, onCropFace,
  loggingFor, setLoggingFor, logNotes, setLogNotes, loggingLoading, submitLogCall, onRefresh }) {
  const PAGE_SIZE = 7;
  const [dSearch, setDSearch] = useState("");
  const [dPage, setDPage] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [docUploading, setDocUploading] = useState(null);
  const docInputRef = useRef(null);
  const [pendingDocDriver, setPendingDocDriver] = useState(null);
  const [pendingDocType, setPendingDocType] = useState(null);

  useEffect(() => { setDPage(0); }, [dSearch]);

  const filtered = driversWithPhone.filter((d) => {
    if (!dSearch.trim()) return true;
    const q = dSearch.toLowerCase();
    return `${d.first_name} ${d.last_name}`.toLowerCase().includes(q) ||
      (d.state || "").toLowerCase().includes(q) ||
      (d.position || "").toLowerCase().includes(q) ||
      (d.cdl_class || "").toLowerCase().includes(q);
  });
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(dPage * PAGE_SIZE, dPage * PAGE_SIZE + PAGE_SIZE);

  function openEdit(d) {
    setEditingId(editingId === d.id ? null : d.id);
    setEditFields({ position_preference: d.position || "", state: d.state || "", cdl_class: d.cdl_class || "" });
  }

  async function saveEdit(driverId) {
    setEditSaving(true);
    try {
      await api.marketRecruiterUpdateDriver(token, driverId, editFields);
      toast.success("Driver profile updated.");
      setEditingId(null);
      await new Promise((r) => setTimeout(r, 300));
    } catch { toast.error("Save failed."); } finally { setEditSaving(false); }
  }

  async function handleDocUpload(file) {
    if (!file || !pendingDocDriver || !pendingDocType) return;
    setDocUploading(pendingDocDriver + pendingDocType);
    try {
      await api.marketSafetyUploadDoc(token, pendingDocDriver, pendingDocType, file);
      toast.success(`${pendingDocType.toUpperCase()} uploaded.`);
    } catch { toast.error("Upload failed."); } finally {
      setDocUploading(null); setPendingDocDriver(null); setPendingDocType(null);
    }
  }

  return (
    <div>
      <input
        ref={docInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleDocUpload(f); e.target.value = ""; }}
      />

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Pipeline</p>
          <h3 className="font-head text-xl font-black uppercase tracking-[0.04em] text-white">Driver Profiles</h3>
        </div>
        <span className="font-head text-xs uppercase tracking-[0.15em] px-3 py-1"
          style={{ background: "rgba(255,77,0,0.1)", color: ACCENT, border: "1px solid rgba(255,77,0,0.2)" }}>
          {driversWithPhone.length} Total
        </span>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: "rgba(255,255,255,0.2)" }} />
        <input
          type="text"
          data-testid="recruiter-driver-search"
          placeholder="Search by name, state, CDL class, position…"
          value={dSearch}
          onChange={(e) => setDSearch(e.target.value)}
          className="w-full bg-[#111] text-white text-sm pl-9 pr-4 py-2.5 focus:outline-none"
          style={{ border: "1px solid rgba(255,255,255,0.08)" }}
        />
        {dSearch && <button onClick={() => setDSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"><X className="w-3.5 h-3.5" /></button>}
      </div>

      {driversWithPhone.length === 0 ? (
        <div className="flex flex-col items-center py-12 text-center" style={{ background: MID, border: "1px solid rgba(255,255,255,0.06)" }}>
          <Truck className="w-10 h-10 mb-3 opacity-20" style={{ color: ACCENT }} />
          <p className="font-head text-sm text-white/30 uppercase tracking-[0.2em]">No drivers yet</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {paged.map((d) => {
              const mStatus = d.market_status || "applied";
              const review = d.safety_review;
              const sc = mStatus === "approved"
                ? { bg: "rgba(34,197,94,0.1)", text: GREEN_SUCCESS, border: "rgba(34,197,94,0.3)" }
                : mStatus === "not_approved"
                  ? { bg: "rgba(239,68,68,0.1)", text: "#ef4444", border: "rgba(239,68,68,0.3)" }
                  : { bg: "rgba(255,77,0,0.1)", text: ACCENT, border: "rgba(255,77,0,0.3)" };
              const isEditing = editingId === d.id;

              return (
                <div key={d.id} style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }}>
                  <div className="p-4 flex gap-4 items-start">
                    {/* Photo */}
                    <div className="shrink-0 w-14 h-14 overflow-hidden"
                      style={{ background: "rgba(255,77,0,0.08)", border: "1px solid rgba(255,77,0,0.2)" }}>
                      {d.market_photo_path
                        ? <img src={`/api/market/photo/${d.id}`} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center font-head text-lg font-black" style={{ color: ACCENT }}>
                            {(d.first_name || "?")[0]}
                          </div>
                      }
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="font-head text-sm font-bold text-white">{d.first_name} {d.last_name}</p>
                        <span className="font-head text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5"
                          style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}>
                          {mStatus.replace("_", " ")}
                        </span>
                        {d.contact_opted_out && (
                          <span data-testid={`recruiter-optout-badge-${d.id}`}
                            className="font-head text-[8px] uppercase tracking-[0.1em] px-1.5 py-0.5"
                            style={{ background: "rgba(239,68,68,0.15)", color: "#f87171", border: "1px solid rgba(239,68,68,0.35)" }}>
                            OPT-OUT
                          </span>
                        )}
                        {review && (
                          <span className="font-head text-[9px] uppercase tracking-[0.08em] px-1.5 py-0.5"
                            style={{ background: "rgba(245,200,66,0.08)", color: GOLD, border: "1px solid rgba(245,200,66,0.25)" }}>
                            Safety: {review.reviewer_name}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-white/40">{d.state} · {d.cdl_class || "CDL-A"} · {d.position || "—"}</p>
                      {d.phone && <p className="text-[11px] font-mono mt-1" style={{ color: d.contact_opted_out ? "#555" : GOLD }}>{d.phone}</p>}

                      {/* Doc badges */}
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {[{ k: "CDL", v: d.has_cdl }, { k: "MED", v: d.has_medical }, { k: "MVR", v: d.has_mvr }, { k: "PSP", v: d.has_psp }].map((doc) => (
                          <span key={doc.k} className="font-head text-[8px] uppercase px-1.5 py-0.5"
                            style={{ background: doc.v ? "rgba(255,77,0,0.1)" : "rgba(255,255,255,0.03)", color: doc.v ? ACCENT : "#444", border: `1px solid ${doc.v ? "rgba(255,77,0,0.3)" : "#222"}` }}>
                            {doc.v ? "✓" : "○"} {doc.k}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button onClick={() => onToggleVisibility(d.id, !d.visible_on_market)}
                        data-testid={`recruiter-visibility-${d.id}`}
                        className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border transition-colors text-center"
                        style={{ borderColor: d.visible_on_market ? "rgba(34,197,94,0.4)" : "rgba(255,255,255,0.15)", color: d.visible_on_market ? GREEN_SUCCESS : "rgba(255,255,255,0.5)", background: d.visible_on_market ? "rgba(34,197,94,0.08)" : "transparent" }}>
                        {d.visible_on_market ? "Visible" : "Hidden"}
                      </button>
                      <button onClick={() => { setLoggingFor(loggingFor === d.id ? null : d.id); setLogNotes(""); }}
                        data-testid={`recruiter-log-call-${d.id}`}
                        className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border transition-colors"
                        style={{ borderColor: "rgba(74,222,128,0.3)", color: "#4ADE80", background: loggingFor === d.id ? "rgba(74,222,128,0.1)" : "transparent" }}>
                        Note
                      </button>
                      {d.public_slug && (
                        <button
                          data-testid={`recruiter-share-link-${d.id}`}
                          onClick={() => {
                            const url = `https://hylascompanion.com/driver/${d.public_slug}`;
                            navigator.clipboard.writeText(url).then(() => toast.success("Share link copied"));
                          }}
                          className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border transition-colors flex items-center gap-1"
                          style={{ borderColor: "rgba(255,77,0,0.3)", color: ACCENT, background: "transparent" }}>
                          <Share2 className="w-3 h-3" /> Share
                        </button>
                      )}
                      {d.contact_opted_out ? (
                        <button
                          data-testid={`recruiter-revoke-optout-${d.id}`}
                          onClick={async () => { await api.marketRevokeOptOut(token, d.id).catch(() => {}); onRefresh?.(); toast.success("Contact re-enabled"); }}
                          className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border transition-colors"
                          style={{ borderColor: "rgba(74,222,128,0.3)", color: "#4ADE80", background: "transparent" }}>
                          <RotateCcw className="w-3 h-3 inline mr-1" />Re-enable
                        </button>
                      ) : (
                        <button
                          data-testid={`recruiter-log-optout-${d.id}`}
                          onClick={async () => { const t = window.confirm("Log STOP reply?\nCancel = log call opt-out instead") ? "sms_stop" : "call_opt_out"; await api.marketLogOptOut(token, d.id, t).catch(() => {}); onRefresh?.(); toast.success(t === "sms_stop" ? "STOP logged" : "Call opt-out logged"); }}
                          className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border transition-colors"
                          style={{ borderColor: "rgba(239,68,68,0.3)", color: "#f87171", background: "transparent" }}>
                          <PhoneOff className="w-3 h-3 inline mr-1" />Opt-Out
                        </button>
                      )}
                      <button onClick={() => openEdit(d)}
                        data-testid={`recruiter-edit-${d.id}`}
                        className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border transition-colors"
                        style={{ borderColor: isEditing ? "rgba(255,77,0,0.5)" : "rgba(255,255,255,0.12)", color: isEditing ? ACCENT : "rgba(255,255,255,0.4)", background: isEditing ? "rgba(255,77,0,0.08)" : "transparent" }}>
                        {isEditing ? "Close" : "Edit ▾"}
                      </button>
                    </div>
                  </div>

                  {/* Log call inline */}
                  {loggingFor === d.id && (
                    <div className="px-4 pb-3 flex gap-2" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                      <input type="text" placeholder="Add a note (visible only to recruiters)…" value={logNotes}
                        onChange={(e) => setLogNotes(e.target.value)}
                        className="flex-1 bg-[#111] border border-white/10 text-white text-xs px-2 py-1.5 focus:outline-none mt-3"
                      />
                      <button onClick={() => submitLogCall(d.id, `${d.first_name} ${d.last_name}`)} disabled={loggingLoading}
                        className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.1em] disabled:opacity-60 mt-3 hover:brightness-110"
                        style={{ background: "#4ADE80", color: DARK }}>Save</button>
                      <button onClick={() => { setLoggingFor(null); setLogNotes(""); }} className="px-2 py-1.5 font-head text-[9px] text-white/30 hover:text-white/60 mt-3">✕</button>
                    </div>
                  )}

                  {/* Edit panel */}
                  {isEditing && (
                    <div className="px-4 pb-4 space-y-3" style={{ borderTop: "1px solid rgba(255,77,0,0.15)", background: "rgba(255,77,0,0.03)" }}>
                      <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 pt-3">Edit Profile</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div>
                          <label className="block text-[9px] uppercase tracking-[0.15em] text-white/30 mb-1">Position</label>
                          <input type="text" value={editFields.position_preference || ""}
                            onChange={(e) => setEditFields((f) => ({ ...f, position_preference: e.target.value }))}
                            placeholder="OTR, Local, Regional…"
                            className="w-full bg-[#111] border border-white/10 text-white text-xs px-2 py-1.5 focus:outline-none focus:border-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] uppercase tracking-[0.15em] text-white/30 mb-1">State</label>
                          <input type="text" value={editFields.state || ""}
                            onChange={(e) => setEditFields((f) => ({ ...f, state: e.target.value }))}
                            placeholder="IL, TX, GA…"
                            className="w-full bg-[#111] border border-white/10 text-white text-xs px-2 py-1.5 focus:outline-none focus:border-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] uppercase tracking-[0.15em] text-white/30 mb-1">CDL Class</label>
                          <input type="text" value={editFields.cdl_class || ""}
                            onChange={(e) => setEditFields((f) => ({ ...f, cdl_class: e.target.value }))}
                            placeholder="CDL-A, CDL-B…"
                            className="w-full bg-[#111] border border-white/10 text-white text-xs px-2 py-1.5 focus:outline-none focus:border-orange-500"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => saveEdit(d.id)} disabled={editSaving}
                          className="px-4 py-1.5 font-head text-[9px] uppercase tracking-[0.15em] disabled:opacity-60 hover:brightness-110 transition-all"
                          style={{ background: ACCENT, color: "white" }}>
                          {editSaving ? "Saving…" : "Save Changes"}
                        </button>
                        {[
                          { label: "CDL Front",      type: "cdl" },
                          { label: "CDL Back",       type: "cdl_back" },
                          { label: "Prev CDL 1",     type: "previous_cdl_1" },
                          { label: "Prev CDL 2",     type: "previous_cdl_2" },
                          { label: "Upload MVR",     type: "mvr" },
                          { label: "Upload PSP",     type: "psp" },
                          { label: "Upload Clearinghouse", type: "clearinghouse" },
                        ].map((doc) => (
                          <button key={doc.type}
                            disabled={!!docUploading}
                            onClick={() => { setPendingDocDriver(d.id); setPendingDocType(doc.type); docInputRef.current?.click(); }}
                            className="px-4 py-1.5 font-head text-[9px] uppercase tracking-[0.15em] border disabled:opacity-60 hover:border-orange-500/50 transition-colors"
                            style={{ borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)" }}>
                            {docUploading === d.id + doc.type ? "Uploading…" : doc.label}
                          </button>
                        ))}
                        {d.has_cdl && !d.market_photo_path && (
                          <button onClick={() => onCropFace(d.id)}
                            className="px-4 py-1.5 font-head text-[9px] uppercase tracking-[0.15em] hover:brightness-110 transition-all"
                            style={{ background: "rgba(255,77,0,0.15)", color: ACCENT, border: "1px solid rgba(255,77,0,0.3)" }}>
                            Crop Face Photo
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 px-1">
              <span className="text-[10px] text-white/30 font-head uppercase tracking-[0.1em]">
                {filtered.length} driver{filtered.length !== 1 ? "s" : ""} · page {dPage + 1} of {totalPages}
              </span>
              <div className="flex gap-1">
                <button onClick={() => setDPage((p) => Math.max(0, p - 1))} disabled={dPage === 0}
                  className="w-7 h-7 flex items-center justify-center disabled:opacity-30 text-white/40 hover:text-white"
                  style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button key={i} onClick={() => setDPage(i)}
                    className="w-7 h-7 flex items-center justify-center font-head text-[10px] transition-colors"
                    style={{ background: i === dPage ? ACCENT : "transparent", color: i === dPage ? "white" : "rgba(255,255,255,0.3)", border: `1px solid ${i === dPage ? ACCENT : "rgba(255,255,255,0.1)"}` }}>
                    {i + 1}
                  </button>
                ))}
                <button onClick={() => setDPage((p) => Math.min(totalPages - 1, p + 1))} disabled={dPage === totalPages - 1}
                  className="w-7 h-7 flex items-center justify-center disabled:opacity-30 text-white/40 hover:text-white"
                  style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── RECRUITER HUB ─────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
function RecruiterHub({ token, drivers, onToggleVisibility, onCropFace, onRefresh }) {
  const [activeRoom, setActiveRoom] = useState("carriers_recruiters");
  const [hubView, setHubView]       = useState("chat"); // "chat" | "carriers"
  const [fullDrivers, setFullDrivers] = useState([]);
  const [outreachLog, setOutreachLog] = useState([]);
  const [showOutreachLog, setShowOutreachLog] = useState(false);
  const [loggingFor, setLoggingFor] = useState(null);
  const [logNotes, setLogNotes] = useState("");
  const [loggingLoading, setLoggingLoading] = useState(false);

  useEffect(() => {
    api.marketGetDriversFull(token).then(setFullDrivers).catch(() => {});
    api.marketGetOutreachLog(token).then((r) => setOutreachLog(r.logs || [])).catch(() => {});
  }, [token]);

  function handlePhotoUpdated(driverId, photoPath) {
    setFullDrivers((prev) =>
      prev.map((d) => d.id === driverId ? { ...d, market_photo_path: photoPath } : d)
    );
  }

  async function submitLogCall(driverId, driverName) {
    setLoggingLoading(true);
    try {
      const entry = await api.marketLogOutreach(token, { driver_id: driverId, driver_name: driverName, notes: logNotes });
      setOutreachLog((prev) => [entry, ...prev]);
      toast.success("Call logged.");
      setLoggingFor(null);
      setLogNotes("");
    } catch { toast.error("Failed to log call."); } finally { setLoggingLoading(false); }
  }

  // Merge phone data + opt-out fields from fullDrivers into driver records
  const driversWithPhone = drivers.map((d) => {
    const full = fullDrivers.find((f) => f.id === d.id);
    return full ? { ...d, phone: full.phone, contact_opted_out: full.contact_opted_out, opt_out_type: full.opt_out_type, opt_out_date: full.opt_out_date } : d;
  });

  const ROOMS = [
    { key: "carriers_recruiters", label: "Carriers", icon: Building2, color: ACCENT, desc: "Chat with carriers placing bids" },
    { key: "recruiters_safety",   label: "Safety",   icon: Shield,    color: GOLD,   desc: "Coordinate with safety team" },
    { key: "drivers_recruiters",  label: "Drivers",  icon: Truck,     color: ACCENT, desc: "Talk directly with drivers" },
  ];

  const activeRoomMeta = ROOMS.find((r) => r.key === activeRoom);

  return (
    <div className="space-y-6">
      {/* Section header */}
      <div>
        <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Recruiter Hub</p>
        <h2 className="font-head text-2xl font-black uppercase tracking-[0.04em] text-white">
          Communication Center
        </h2>
      </div>

      {/* Top-level view toggle */}
      <div className="flex gap-2">
        <button
          data-testid="recruiter-view-chat"
          onClick={() => setHubView("chat")}
          className="flex items-center gap-1.5 px-4 py-2 font-head text-[10px] uppercase tracking-[0.15em] transition-all"
          style={{
            background: hubView === "chat" ? "rgba(255,77,0,0.15)" : "transparent",
            border: `1px solid ${hubView === "chat" ? "rgba(255,77,0,0.5)" : "rgba(255,255,255,0.12)"}`,
            color: hubView === "chat" ? ACCENT : "rgba(255,255,255,0.45)",
          }}>
          <MessageCircle className="w-3.5 h-3.5" />
          Chat Rooms
        </button>
        <button
          data-testid="recruiter-view-carriers"
          onClick={() => setHubView("carriers")}
          className="flex items-center gap-1.5 px-4 py-2 font-head text-[10px] uppercase tracking-[0.15em] transition-all"
          style={{
            background: hubView === "carriers" ? "rgba(255,77,0,0.15)" : "transparent",
            border: `1px solid ${hubView === "carriers" ? "rgba(255,77,0,0.5)" : "rgba(255,255,255,0.12)"}`,
            color: hubView === "carriers" ? ACCENT : "rgba(255,255,255,0.45)",
          }}>
          <Building2 className="w-3.5 h-3.5" />
          Carrier Network
        </button>
      </div>

      {/* Carrier Network view */}
      {hubView === "carriers" && (
        <div>
          <p className="text-xs mb-4" style={{ color: "rgba(255,255,255,0.35)" }}>
            Browse carrier companies active on the Hylas platform — their equipment, lanes, credentials and fleet photos.
          </p>
          <CarrierDirectory token={token} />
        </div>
      )}

      {/* Chat view */}
      {hubView === "chat" && (
      <>
      <div className="flex gap-2 flex-wrap">
        {ROOMS.map((r) => {
          const Icon = r.icon;
          const isActive = activeRoom === r.key;
          const accentRgb = r.color === ACCENT ? "255,77,0" : "245,200,66";
          return (
            <button
              key={r.key}
              onClick={() => setActiveRoom(r.key)}
              data-testid={`recruiter-chat-tab-${r.key}`}
              className="flex items-center gap-2 px-4 py-2.5 font-head text-xs uppercase tracking-[0.15em] transition-all"
              style={{
                background: isActive ? `rgba(${accentRgb},0.12)` : "transparent",
                border: `1px solid ${isActive ? `rgba(${accentRgb},0.4)` : "rgba(255,255,255,0.08)"}`,
                color: isActive ? r.color : "rgba(255,255,255,0.5)",
                boxShadow: isActive ? `0 0 16px rgba(${accentRgb},0.12)` : "none",
              }}
            >
              <Icon className="w-3.5 h-3.5" />
              {r.label}
            </button>
          );
        })}
      </div>

      {/* Active chat */}
      <ChatRoom
        room={activeRoom}
        roomLabel={`${activeRoomMeta?.label || ""} · Recruiters`}
        token={token}
        hub="recruiter"
      />

      {/* ── Driver Profiles (with search + pagination + edit) ── */}
      <RecruiterDriverList
        driversWithPhone={driversWithPhone}
        token={token}
        onToggleVisibility={onToggleVisibility}
        onCropFace={onCropFace}
        loggingFor={loggingFor}
        setLoggingFor={setLoggingFor}
        logNotes={logNotes}
        setLogNotes={setLogNotes}
        loggingLoading={loggingLoading}
        submitLogCall={submitLogCall}
        onRefresh={onRefresh}
      />

      {/* ── Bulk CDL Upload ── */}
      <BulkCDLPanel token={token} fullDrivers={fullDrivers} onPhotoUpdated={handlePhotoUpdated} />
      <BulkMedicalPanel token={token} fullDrivers={fullDrivers} />

      {/* ── Driver Status Management (read-only — Safety approves) ── */}
      {(() => {
        const safetyReviewed = driversWithPhone.filter((d) => d.safety_review || d.market_status === "approved" || d.market_status === "not_approved");
        const approved = safetyReviewed.filter((d) => d.market_status === "approved");
        const notApproved = safetyReviewed.filter((d) => d.market_status === "not_approved");
        const pending = driversWithPhone.filter((d) => d.market_status === "applied" || !d.market_status);
        return (
          <div style={{ background: MID, border: "1px solid rgba(56,189,248,0.15)" }}>
            <div className="px-4 py-3 flex items-center gap-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <Shield className="w-4 h-4 shrink-0" style={{ color: "#38BDF8" }} />
              <div className="flex-1">
                <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Driver Status Management</p>
                <p className="text-[10px] text-white/30 mt-0.5">Set by Safety Hub — read-only for Recruiters</p>
              </div>
              <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
                style={{ background: "rgba(34,197,94,0.1)", color: GREEN_SUCCESS, border: "1px solid rgba(34,197,94,0.25)" }}>
                {approved.length} approved
              </span>
              <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
                style={{ background: "rgba(239,68,68,0.08)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)" }}>
                {notApproved.length} not approved
              </span>
            </div>
            <div className="divide-y divide-white/05 max-h-60 overflow-y-auto">
              {pending.map((d) => (
                <div key={d.id} className="px-4 py-2.5 flex items-center gap-3">
                  <p className="flex-1 font-head text-xs text-white/60">{d.first_name} {d.last_name}</p>
                  <span className="font-head text-[8px] uppercase px-2 py-0.5"
                    style={{ background: "rgba(255,77,0,0.08)", color: ACCENT, border: "1px solid rgba(255,77,0,0.2)" }}>Pending Safety Review</span>
                </div>
              ))}
              {[...approved, ...notApproved].map((d) => {
                const isApproved = d.market_status === "approved";
                return (
                  <div key={d.id} className="px-4 py-2.5 flex items-center gap-3">
                    <p className="flex-1 font-head text-xs text-white">{d.first_name} {d.last_name}</p>
                    {d.safety_review && (
                      <span className="text-[9px] text-white/30 font-head uppercase">{d.safety_review.reviewer_name}</span>
                    )}
                    <span className="font-head text-[8px] uppercase px-2 py-0.5"
                      style={{ background: isApproved ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.08)", color: isApproved ? GREEN_SUCCESS : "#ef4444", border: `1px solid ${isApproved ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.2)"}` }}>
                      {isApproved ? "✓ Approved" : "✗ Not Approved"}
                    </span>
                  </div>
                );
              })}
              {driversWithPhone.length === 0 && <p className="px-4 py-6 text-xs text-white/25 text-center">No drivers found.</p>}
            </div>
          </div>
        );
      })()}

      {/* ── Outreach Log ── */}
      <div style={{ background: MID, border: "1px solid rgba(74,222,128,0.2)" }}>
        <button
          data-testid="recruiter-outreach-toggle"
          onClick={() => setShowOutreachLog((v) => !v)}
          className="w-full px-4 py-3 flex items-center gap-3 text-left"
          style={{ borderBottom: showOutreachLog ? "1px solid rgba(255,255,255,0.07)" : "none" }}
        >
          <Phone className="w-4 h-4 shrink-0" style={{ color: "#4ADE80" }} />
          <div className="flex-1">
            <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white">Outreach Log</p>
            <p className="text-[10px] text-white/30 mt-0.5">Team call activity &amp; notes</p>
          </div>
          <span className="font-head text-[9px] px-2 py-0.5 uppercase tracking-[0.1em]"
            style={{ background: "rgba(74,222,128,0.1)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.25)" }}>
            {outreachLog.length} logged
          </span>
          <ChevronDown className="w-4 h-4 text-white/30 transition-transform ml-1" style={{ transform: showOutreachLog ? "rotate(180deg)" : "rotate(0deg)" }} />
        </button>
        {showOutreachLog && (
          <div className="divide-y divide-white/05 max-h-80 overflow-y-auto">
            {outreachLog.length === 0 ? (
              <p className="px-4 py-6 text-xs text-white/25 text-center">No notes logged yet. Use "Note" on any driver.</p>
            ) : (
              outreachLog.map((l) => (
                <div key={l.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-head text-sm font-bold text-white">{l.driver_name}</p>
                      <p className="text-[11px] text-white/40 mt-0.5">{l.notes || <span className="italic text-white/20">No notes</span>}</p>
                      <p className="text-[10px] mt-1" style={{ color: "rgba(255,255,255,0.25)" }}>by {l.logged_by}</p>
                    </div>
                    <span className="text-[9px] text-white/25 shrink-0 mt-0.5">{timeAgo(l.logged_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      </>
      )}  {/* end hubView === "chat" */}
    </div>
  );
}

export { BulkCDLPanel, BulkMedicalPanel, RecruiterDriverList, RecruiterHub };
